'use client';

import React from 'react';
import { DollarSign, TrendingUp, BarChart2, Award } from 'lucide-react';

interface PortfolioStatsProps {
  virtualCash: number;
  openPnL: number;
  realizedPnL: number;
  winRate: number;
  totalTrades: number;
  openPositions: number;
}

export default function PortfolioStats({
  virtualCash,
  openPnL,
  realizedPnL,
  winRate,
  totalTrades,
  openPositions,
}: PortfolioStatsProps) {
  const stats = [
    {
      label: 'Available Cash',
      value: `$${virtualCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: <DollarSign size={16} />,
      color: 'text-primary',
      bg: 'bg-primary/10',
      sub: `${openPositions} open position${openPositions !== 1 ? 's' : ''}`,
    },
    {
      label: 'Open P&L',
      value: `${openPnL >= 0 ? '+' : ''}$${openPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: <TrendingUp size={16} />,
      color: openPnL >= 0 ? 'text-emerald-400' : 'text-red-400',
      bg: openPnL >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10',
      sub: 'Unrealized',
    },
    {
      label: 'Realized P&L',
      value: `${realizedPnL >= 0 ? '+' : ''}$${realizedPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: <BarChart2 size={16} />,
      color: realizedPnL >= 0 ? 'text-emerald-400' : 'text-red-400',
      bg: realizedPnL >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10',
      sub: `${totalTrades} closed trade${totalTrades !== 1 ? 's' : ''}`,
    },
    {
      label: 'Win Rate',
      value: `${winRate.toFixed(1)}%`,
      icon: <Award size={16} />,
      color: winRate >= 50 ? 'text-amber-400' : 'text-muted-foreground',
      bg: winRate >= 50 ? 'bg-amber-500/10' : 'bg-muted/50',
      sub: `${totalTrades} trades closed`,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {stats.map((s) => (
        <div key={s.label} className="bg-card border border-border rounded-2xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground font-500">{s.label}</p>
            <div className={`w-7 h-7 rounded-lg ${s.bg} flex items-center justify-center ${s.color}`}>
              {s.icon}
            </div>
          </div>
          <p className={`text-lg font-700 tabular-nums ${s.color}`}>{s.value}</p>
          <p className="text-xs text-muted-foreground">{s.sub}</p>
        </div>
      ))}
    </div>
  );
}
